import { ViperSelect } from "@/components";
import { IProject, IProjectSearchResult, IProjectTeamRole } from "@/interfaces";
import {
  useGetProjectQuery,
  useSearchProjectsQuery,
  useUpdateProjectTeamMutation,
} from "@/services";
import { Form, Modal, Row, Select, Space, Typography } from "antd";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";
import { v4 } from "uuid";

import { StringParam, useQueryParams } from "use-query-params";

interface IProps {
  modalOpen: boolean;
  setShowCopyModal(value: boolean): void;
  project: IProject;
  refetchProject(): void;
}

export const TeamCopyModal = ({
  modalOpen,
  setShowCopyModal,
  project,
  refetchProject,
}: IProps) => {
  const intl = useIntl();
  const { Option } = Select;
  const { Text } = Typography;
  const { confirm } = Modal;
  const [filteredData, setFilteredData] = useState<IProjectSearchResult[]>([]);
  const [form] = Form.useForm();
  const [updateProjectTeam, { isLoading: isCopying }] =
    useUpdateProjectTeamMutation();
  const [selectedProject, setSelectedProject] = useState<string>();

  const [queryParams, setQueryParams] = useQueryParams({
    projectType: StringParam,
    query: StringParam,
  });

  const { data: searchResults, isFetching } = useSearchProjectsQuery(
    queryParams,
    {
      skip: !queryParams || !queryParams.query,
    }
  );

  const { data: sourceProject } = useGetProjectQuery(selectedProject, {
    skip: !selectedProject,
  });

  const showConfirm = () => {
    confirm({
      title: intl.formatMessage({ id: "project.copyProjectTeamWarningTitle" }),
      content: intl.formatMessage({ id: "project.copyProjectTeamWarning" }),
      onOk() {
        copyData();
      },
    });
  };

  const copyData = () => {
    const updatedProjectTeam = {
      projectRoles: (sourceProject.projectRoles || []).map(
        (x: IProjectTeamRole) => ({
          ...x,
          id: v4(),
        })
      ),
      projectViewers: sourceProject.projectViewers || [],
    };
    updateProjectTeam({ id: project.id, projectTeam: updatedProjectTeam })
      .unwrap()
      .then(() => refetchProject())
      .then(() => {
        toast.success(intl.formatMessage({ id: "general.toastChangesSaved" }));
        setShowCopyModal(false);
      });
  };

  const handleSave = (): void => {
    form.validateFields().then(() => {
      if (!sourceProject) {
        toast.error(
          intl.formatMessage({ id: "general.toastSomethingWentWrong" })
        );
        return;
      }
      if (
        project?.projectRoles?.length > 0 ||
        project?.projectViewers?.length > 0
      ) {
        showConfirm();
      } else {
        copyData();
      }
    });
  };

  const setQuery = (query: string): void => {
    setQueryParams(
      { projectType: project.projectType, query: query },
      "replace"
    );
  };

  useEffect(() => {
    if (searchResults?.data) {
      const filtered = searchResults.data
        .filter(
          (projects: { id: string; isMigrated: boolean }) =>
            projects.id !== project.id && !projects.isMigrated
        )
        .map((searchProject: IProjectSearchResult) => ({
          projectName: searchProject.projectName,
          id: searchProject.id,
          projectNumber: searchProject.projectNumber,
        }));
      if (filtered !== null) {
        setFilteredData(filtered);
      }
    }
  }, [project.id, searchResults, queryParams]);

  if (!modalOpen) {
    return <Form form={form} component={false} />;
  }

  return (
    <Modal
      title={intl.formatMessage({
        id: "project.copyProjectTeam",
      })}
      centered
      open={modalOpen}
      onCancel={() => setShowCopyModal(false)}
      onOk={handleSave}
      okButtonProps={{
        disabled: isCopying,
      }}
      okText={intl.formatMessage({
        id: "general.save",
      })}
    >
      <Form form={form} name="projectTeamCopyForm">
        <Form.Item name="projectSelect">
          <Row>
            <ViperSelect
              data-test-id={`copy-project-select`}
              onChange={setSelectedProject}
              customOptions={filteredData?.map(
                ({ id, projectName, projectNumber }) => (
                  <Option key={id} label={projectName} value={id}>
                    <Space direction="vertical" size={0}>
                      <Text className="font-bold">{projectName}</Text>
                      {!!projectNumber && (
                        <Text type="secondary">
                          <small>
                            {intl.formatMessage({
                              id: "projects.projectNumber",
                            })}
                            : {projectNumber}
                          </small>
                        </Text>
                      )}
                    </Space>
                  </Option>
                )
              )}
              allowMultipleSelections={false}
              onFetchSuggestions={setQuery}
              placeholder={intl.formatMessage({ id: "general.search" })}
              loading={isFetching}
              optionLabelProp="label"
            />
          </Row>
        </Form.Item>
      </Form>
    </Modal>
  );
};
